/**
 * Responsive image URL helper.
 *
 * Generates srcSet + sizes for Cloudinary and Unsplash sources so that
 * mobile devices load small (400/800w) variants and desktop load up to 1920w,
 * with automatic WebP/AVIF format negotiation and quality.
 *
 * - The original asset is NEVER modified (Cloudinary stores the master file as backup).
 * - Cloudinary: f_auto, q_auto, c_limit (or c_fill+g_auto if aspect supplied)
 * - Unsplash:   auto=format,compress, fm=webp, q=82
 * - Other URLs (relative, /downloads, data:, etc.): passthrough.
 *
 * @param {string} rawUrl
 * @param {object} opts
 * @param {number[]} [opts.widths]  default [400, 800, 1200, 1920]
 * @param {string}   [opts.aspect]  e.g. "16/9", "4/3", "9/16"  → enables smart-fill crop
 * @param {string}   [opts.fit]     "limit" (default, no crop) | "fill" (crop with g_auto)
 * @param {string}   [opts.sizes]   custom `sizes` attribute (default sensible)
 * @returns {{ src: string, srcSet?: string, sizes?: string } | null}
 */
const CLOUDINARY_RE = /(res\.cloudinary\.com\/[^/]+\/image\/upload\/)/;
const UNSPLASH_RE = /images\.unsplash\.com/;
const PEXELS_RE = /images\.pexels\.com/;

const DEFAULT_WIDTHS = [400, 800, 1200, 1920];
const DEFAULT_SIZES =
  "(max-width: 640px) 100vw, (max-width: 1024px) 80vw, (max-width: 1536px) 60vw, 1200px";

function injectCloudinary(url, w, opts) {
  const { aspect, fit } = opts;
  // Decide crop mode:
  //  - aspect supplied → c_fill + g_auto (smart focal detection, no important content lost)
  //  - else            → c_limit (preserves original aspect, just resizes within bounds)
  const cropMode = aspect ? "fill" : fit || "limit";
  const tx = [`f_auto`, `q_auto`, `c_${cropMode}`, `w_${w}`];
  if (cropMode === "fill") tx.push("g_auto");
  if (aspect) tx.push(`ar_${aspect.replace("/", ":")}`);
  return url.replace(CLOUDINARY_RE, `$1${tx.join(",")}/`);
}

function injectUnsplash(url, w) {
  try {
    const u = new URL(url);
    u.searchParams.set("w", String(w));
    u.searchParams.set("q", "82");
    u.searchParams.set("auto", "format,compress");
    u.searchParams.set("fm", "webp");
    return u.toString();
  } catch {
    return url;
  }
}

function injectPexels(url, w) {
  try {
    const u = new URL(url);
    u.searchParams.set("w", String(w));
    u.searchParams.set("auto", "compress");
    u.searchParams.set("cs", "tinysrgb");
    u.searchParams.set("dpr", "1");
    return u.toString();
  } catch {
    return url;
  }
}

export function responsiveImage(rawUrl, opts = {}) {
  if (!rawUrl || typeof rawUrl !== "string") return null;
  const widths = opts.widths || DEFAULT_WIDTHS;
  const isCloud = CLOUDINARY_RE.test(rawUrl);
  const isUnsplash = UNSPLASH_RE.test(rawUrl);
  const isPexels = PEXELS_RE.test(rawUrl);

  if (!isCloud && !isUnsplash && !isPexels) {
    return { src: rawUrl };
  }

  const buildOne = (w) => {
    if (isCloud) return injectCloudinary(rawUrl, w, opts);
    if (isUnsplash) return injectUnsplash(rawUrl, w);
    return injectPexels(rawUrl, w);
  };

  const srcSet = widths.map((w) => `${buildOne(w)} ${w}w`).join(", ");
  const fallbackWidth = widths[Math.max(0, widths.length - 2)];
  const src = buildOne(fallbackWidth);
  const sizes = opts.sizes || DEFAULT_SIZES;

  return { src, srcSet, sizes };
}

/** Helper that picks a single optimized URL (no srcSet) — useful for og:image, backgrounds. */
export function singleOptimizedUrl(rawUrl, width = 1200, opts = {}) {
  const r = responsiveImage(rawUrl, { ...opts, widths: [width] });
  return r ? r.src : rawUrl;
}
